"use client";

/**
 * Region visuals for the calling campaigns: a chip on every row, a strip of
 * clickable region cards (each a stacked bar of where outreach stands), and a
 * ranked country list for the regions in view.
 */
import * as React from "react";
import { Globe2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { fmtNum, fmtPct } from "@/lib/insights/format";
import { REGIONS, regionMeta } from "@/lib/regions";
import { BarList } from "@/components/staff/insights/charts/BarList";

export function RegionChip({ region, className }: { region: string | null | undefined; className?: string }) {
  const m = regionMeta(region);
  return (
    <span title={m.long}
      className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-200", className)}>
      <span aria-hidden className="size-2 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

export interface Bucket {
  key: string;
  label: string;
  /** Tailwind background class for the bar segment and legend swatch. */
  className: string;
}

export interface RegionCardData {
  region: string;
  total: number;
  values: Record<string, number>;
}

/**
 * One card per region (always all five, in a fixed order, so nothing jumps as
 * filters change). The counts ignore the region filter itself, so a selected
 * region never hides the others. Click a card to add or remove that region.
 */
export function RegionBreakdown({
  items, buckets, selected, onToggle, noun = "people",
}: {
  items: RegionCardData[];
  buckets: Bucket[];
  selected: string[];
  onToggle: (region: string) => void;
  noun?: string;
}) {
  const grand = items.reduce((n, r) => n + r.total, 0);
  const anySelected = selected.length > 0;
  return (
    <section aria-label="By region" className="rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] sm:p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          <Globe2 className="size-4 text-[#007a8a]" aria-hidden /> By region
          <span className="font-normal text-slate-500">· click to filter, pick as many as you like</span>
        </h3>
        <ul className="flex flex-wrap items-center gap-3 text-xs text-slate-500" aria-label="Legend">
          {buckets.map((b) => (
            <li key={b.key} className="flex items-center gap-1.5"><span aria-hidden className={cn("size-2.5 rounded-sm", b.className)} />{b.label}</li>
          ))}
        </ul>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {REGIONS.map((meta) => {
          const r = items.find((x) => x.region === meta.key) ?? { region: meta.key, total: 0, values: {} };
          const on = selected.includes(meta.key);
          const share = grand ? (r.total / grand) * 100 : 0;
          return (
            <button key={meta.key} type="button" aria-pressed={on} onClick={() => onToggle(meta.key)} title={meta.long}
              className={cn(
                "group relative overflow-hidden rounded-xl border bg-white p-3 text-left transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0f6f7c]",
                on ? "border-transparent shadow-md ring-2" : "border-slate-200 hover:border-slate-300 hover:shadow-sm",
                anySelected && !on && "opacity-55 hover:opacity-100",
              )}
              style={on ? { boxShadow: `0 0 0 2px ${meta.color}` } : undefined}>
              <span aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ background: meta.color }} />
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold tracking-wide" style={{ color: meta.color }}>{meta.label}</span>
                <span className="text-[11px] tabular-nums text-slate-400">{fmtPct(share)}</span>
              </div>
              <p key={r.total} className="mt-1 animate-[fadeUp_.3s_ease-out] text-2xl font-bold tabular-nums text-slate-900">{fmtNum(r.total)}</p>
              <p className="truncate text-[11px] text-slate-500">{meta.key === "Unassigned" ? "no country on file" : `${noun} · ${meta.long}`}</p>
              <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden>
                {r.total > 0 && buckets.map((b) => {
                  const v = r.values[b.key] ?? 0;
                  return v > 0 ? <span key={b.key} className={cn(b.className, "transition-[width] duration-500 ease-out")} style={{ width: `${(v / r.total) * 100}%` }} /> : null;
                })}
              </div>
              <p className="sr-only">
                {buckets.map((b) => `${b.label}: ${r.values[b.key] ?? 0}`).join(", ")}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-x-2 text-[11px] tabular-nums text-slate-500">
                {buckets.map((b) => (
                  <span key={b.key} title={b.label} className="flex items-center gap-1">
                    <span aria-hidden className={cn("size-1.5 rounded-full", b.className)} />{fmtNum(r.values[b.key] ?? 0)}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** Top countries in view; click one to add it to the country filter. */
export function CountryBars({
  items, selected, onToggle, limit = 8, regionOf,
}: {
  items: { country: string; count: number; region?: string | null }[];
  selected: string[];
  onToggle: (country: string) => void;
  limit?: number;
  regionOf?: (country: string) => string | null | undefined;
}) {
  const [all, setAll] = React.useState(false);
  const shown = all ? items : items.slice(0, limit);
  return (
    <div>
      <BarList
        items={shown.map((c) => {
          const region = c.region ?? regionOf?.(c.country);
          const meta = region ? regionMeta(region) : null;
          return {
            key: c.country,
            value: c.count,
            label: (
              <span className="flex items-center gap-1.5">
                {meta && <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ background: meta.color }} />}
                <span className={cn(selected.includes(c.country) && "font-semibold text-[#0b5560]")}>{c.country}</span>
                {selected.includes(c.country) && <span className="text-[10px] font-semibold uppercase text-[#007a8a]">filtered</span>}
              </span>
            ),
          };
        })}
        onPick={(it) => onToggle(it.key)}
        empty="No countries for these filters."
      />
      {items.length > limit && (
        <button type="button" onClick={() => setAll((v) => !v)}
          className="mt-1 rounded-md px-2 py-1 text-xs font-medium text-[#007a8a] hover:bg-[#e6f0f1] focus-visible:outline-2 focus-visible:outline-[#0f6f7c]">
          {all ? "Show fewer" : `Show all ${items.length} countries`}
        </button>
      )}
    </div>
  );
}
